import { extractSelectOptions, fieldFromName, fieldName } from '@alliance-disposal/wql';
import { SourQuerySchema } from './schema';
export function removeUnusedFiltersAndConvertToArray(obj) {
    const result = [];
    function search(obj) {
        if (typeof obj === 'object' && obj !== null) {
            if (Object.prototype.hasOwnProperty.call(obj, 'field')) {
                if (obj.value !== undefined && (obj.operator !== undefined || obj.type === 'BOOLEAN')) {
                    result.push(obj);
                }
            }
            for (const key in obj) {
                search(obj[key]);
            }
        }
    }
    search(obj);
    return result;
}
export const countFilterFields = (obj) => {
    const filters = removeUnusedFiltersAndConvertToArray(obj.filters);
    let count = filters.length;
    // IF TYPE IS SELECTED, ADD 1 TO COUNT
    if (obj.type) {
        count++;
    }
    // IF SORT IS SELECTED, ADD 1 TO COUNT
    if (obj?.sort?.field !== undefined && obj?.sort?.direction) {
        count++;
    }
    return { totalCount: count, filterCount: filters.length };
};
export const getHumanReadableGrouping = (index) => {
    if (index.includes('service-grouping')) {
        return 'SERVICE GROUPINGS';
    }
    if (index.includes('order')) {
        return 'ORDERS';
    }
    if (index.includes('receivable')) {
        return 'RECEIVABLES';
    }
    if (index.includes('payable')) {
        return 'PAYABLES';
    }
    if (index.includes('customer')) {
        return 'CUSTOMERS';
    }
    if (index.includes('vendor')) {
        return 'VENDORS';
    }
    if (index.includes('pricing-zone')) {
        return 'PRICING ZONES';
    }
    if (index.includes('hauler-pricing-zone')) {
        return 'HAULER PRICING ZONES';
    }
    if (index.includes('lead')) {
        return 'LEADS';
    }
    if (index.includes('disposal-site')) {
        return 'DISPOSAL SITES';
    }
    if (index.includes('dumpster')) {
        return 'DUMPSTERS';
    }
    return '';
};
export const isNumberStrict = (value) => {
    return (typeof value === 'number' && !isNaN(value)) || value instanceof Number;
};
// RETURNS THE VALUE IN "" OR NULL IF NON EXIST
const findWordInQuotes = (input) => {
    const matches = input.match(/"([^"]*)"/);
    return matches ? matches[1] : null;
};
export const handlePrepareQuery = (query, application) => {
    const search = query?.query || '';
    const searchQuery = {
        query: query?.query || '',
        restrictToFields: query?.restrictToFields || [],
    };
    let multiMatchType = undefined;
    let strippedSearchString = search;
    if (search?.includes(':') && application) {
        const inputArray = [search.slice(0, search.indexOf(':')), search.slice(search.indexOf(':') + 1)];
        const searchableField = inputArray[0];
        const searchValue = inputArray[1]?.trim();
        const fields = fieldFromName(searchableField, {
            application: application,
        });
        const restrictedFields = new Set();
        fields.map((field) => {
            restrictedFields.add(field.field);
        });
        searchQuery.query = searchValue;
        searchQuery.restrictToFields = Array.from(restrictedFields);
        strippedSearchString = searchValue;
    }
    if (Boolean(findWordInQuotes(strippedSearchString)) && application) {
        multiMatchType = 'phrase_prefix';
        searchQuery.query = strippedSearchString.replace(/"/g, '');
    }
    return { query: searchQuery, multiMatchType: multiMatchType };
};
// CHECKS TO SEE IF QUERY IS SAME AS DEFAULT FILTERS
export const handleCheckIfEqualToDefault = (queryData, defaultFilters) => {
    if (!defaultFilters)
        return false;
    const blankQueryObject = {
        filters: {},
        query: { query: '', restrictToFields: [] },
        sort: { field: undefined, direction: 'DESC' },
        type: '',
    };
    const fullDefaultFilterObject = {
        ...blankQueryObject,
        ...defaultFilters.query,
    };
    if (!fullDefaultFilterObject.query.restrictToFields) {
        fullDefaultFilterObject.query.restrictToFields = [];
    }
    const fullQueryData = {
        ...blankQueryObject,
        ...queryData,
    };
    if (!fullQueryData.query.restrictToFields) {
        fullQueryData.query.restrictToFields = [];
    }
    fullQueryData.filters = removeUnusedFiltersAndConvertToArray(fullQueryData.filters);
    fullDefaultFilterObject.filters = removeUnusedFiltersAndConvertToArray(fullDefaultFilterObject.filters);
    return JSON.stringify(fullQueryData) === JSON.stringify(fullDefaultFilterObject);
};
const flattenObjectPaths = (obj, parentKey = '') => {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
        const newKey = parentKey ? `${parentKey}.${key}` : key;
        if (value && typeof value === 'object' && !('field' in value && 'type' in value)) {
            Object.assign(result, flattenObjectPaths(value, newKey));
        }
        else {
            result[newKey] = value;
        }
    }
    return result;
};
export const handleTransformAttributesToFilters = (attributes, application, filters) => {
    const result = {};
    attributes.forEach((attribute) => {
        const keys = attribute.field.split('.');
        const baseFieldObj = {
            field: attribute.field,
            type: attribute.type,
            useKeywordSubfield: attribute.useKeywordSubfield,
            format: attribute.format || undefined,
            isExpanded: false,
            operator: undefined,
            value: undefined,
            fieldName: fieldName(attribute),
            options: attribute.type === 'STRING' ? extractSelectOptions(attribute.options, { application }) : [],
        };
        // Flatten the filters object to make it easier to check for matching fields
        const flatPartialFilters = filters ? flattenObjectPaths(filters) : {};
        // If there's a matching partial filter for this field
        const mergedFieldObj = flatPartialFilters[attribute.field]
            ? {
                ...baseFieldObj,
                ...flatPartialFilters[attribute.field],
                isExpanded: flatPartialFilters[attribute.field].operator || flatPartialFilters[attribute.field].value !== undefined
                    ? true
                    : false,
            }
            : baseFieldObj;
        if (keys.length === 1) {
            result[attribute.field] = mergedFieldObj;
        }
        else {
            let current = result;
            keys.forEach((key, index) => {
                if (index === keys.length - 1) {
                    current[key] = mergedFieldObj;
                }
                else {
                    current[key] = current[key] || {};
                    current = current[key];
                }
            });
        }
    });
    return result;
};
// Type-safe parser that ensures the output matches SourQuery exactly
export const parseSourQuery = (input) => {
    const parsed = SourQuerySchema.parse(input);
    // Type assertion is safe here because we know the structure matches
    // even though Zod can't represent the exact union type constraints
    return parsed;
};
// Alternative: Create a more specific parser with better error handling
export const safeParseSourQuery = (input) => {
    const result = SourQuerySchema.safeParse(input);
    if (result.success) {
        return { success: true, data: result.data };
    }
    return { success: false, error: result.error };
};
// Custom parser function for nuqs that maintains proper typing
export const createSourQueryParser = ({ application }) => {
    if (!application) {
        throw new Error('Application is required');
    }
    return {
        parse: (value) => {
            try {
                const parsed = JSON.parse(value, (_key, val) => {
                    // Check for ISO 8601 strings and convert to Date
                    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(val)) {
                        const date = new Date(val);
                        return isNaN(date.getTime()) ? val : date;
                    }
                    return val;
                });
                const result = safeParseSourQuery(parsed);
                if (result.success) {
                    return result.data;
                }
                console.warn('SourQuery failed zod validation:', result.error);
                return null; // fallback to defaultValue
            }
            catch (e) {
                console.warn('Invalid SourQuery JSON:', value, e);
                return null;
            }
        },
        serialize: (value) => {
            const attributeArray = removeUnusedFiltersAndConvertToArray(value.filters);
            return JSON.stringify({
                ...value,
                filters: handleTransformAttributesToFilters(attributeArray, application, value.filters),
            });
        },
    };
};
export const extractTotalHitsFromEsResponse = (esResponse) => {
    if (isNumberStrict(esResponse.hits.total)) {
        return esResponse.hits.total;
    }
    return esResponse.hits?.total?.value || 0;
};
export const transformArrayToEsResponse = (items, requestPath) => {
    const hits = items.map((item, index) => ({
        _index: requestPath,
        _id: item.id || index.toString(),
        _score: 1,
        _source: item,
    }));
    return {
        hits: {
            total: { value: items.length, relation: 'eq' },
            max_score: 1,
            hits,
        },
        aggregations: {},
    };
};
export const createEmptyEsResponse = () => ({
    hits: {
        total: { value: 0, relation: 'eq' },
        max_score: null,
        hits: [],
    },
    aggregations: {},
    took: 0,
    timed_out: false,
    _shards: { total: 0, successful: 0, skipped: 0, failed: 0 },
});
export const buildSearchResponse = (results, totalHits, page, size, queryType) => ({
    results,
    page,
    size,
    totalPages: Math.ceil(totalHits / size),
    totalHits,
    type: queryType,
});
const STATUS_COMPUTED_FIELD = 'invoiceDetails.status_computed';
/**
 * Enriches _source with real-time status from ES runtime fields for display.
 * Use for receivable/payable search hits so the invoice list shows status_computed (e.g. PAST_DUE) instead of stale _source.invoiceDetails.status.
 */
export function invoiceHitWithComputedStatus(hit) {
    const source = (hit._source ?? {});
    const invoiceDetails = { ...(source.invoiceDetails ?? {}) };
    const statusComputed = hit.fields?.[STATUS_COMPUTED_FIELD]?.[0];
    if (statusComputed !== undefined && statusComputed !== null) {
        invoiceDetails.status_computed = statusComputed;
    }
    return { ...source, invoiceDetails };
}
//# sourceMappingURL=functions.js.map